import { useState } from "react";
import type { ContactAddress } from "../../shared/types.js";
import { FormField } from "../../components/FormField.js";
import {
  required,
  isEmail,
  emailsMatch,
  isBelgianPhone,
  isBelgianPostalCode,
  isBelgianVat,
} from "../../shared/validate.js";

export interface AddressFormErrors {
  [key: string]: string | undefined;
}

export function validateAddressForm(contact: ContactAddress): AddressFormErrors {
  const errors: AddressFormErrors = {};
  const set = (field: string, result: { valid: boolean; message?: string }) => {
    if (!result.valid) errors[field] = result.message;
  };
  set("firstName", required(contact.firstName, "Voornaam"));
  set("lastName", required(contact.lastName, "Achternaam"));
  set("email", isEmail(contact.email));
  set("emailConfirm", required(contact.emailConfirm, "Bevestiging e-mailadres"));
  if (contact.email && contact.emailConfirm) set("emailConfirm", emailsMatch(contact.email, contact.emailConfirm));
  set("phone", isBelgianPhone(contact.phone));
  set("street", required(contact.street, "Straat"));
  set("houseNumber", required(contact.houseNumber, "Huisnummer"));
  set("postalCode", isBelgianPostalCode(contact.postalCode));
  set("city", required(contact.city, "Gemeente"));
  if (contact.vatNumber) set("vatNumber", isBelgianVat(contact.vatNumber));
  return errors;
}

export function AddressForm({
  contact,
  onChange,
  errors,
}: {
  contact: ContactAddress;
  onChange: (c: ContactAddress) => void;
  errors: AddressFormErrors;
}) {
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  function patch(p: Partial<ContactAddress>) {
    onChange({ ...contact, ...p });
  }
  function markTouched(field: string) {
    setTouched((t) => ({ ...t, [field]: true }));
  }
  function err(field: string) {
    return touched[field] ? errors[field] : undefined;
  }

  return (
    <div>
      <h2>Uw gegevens en adres</h2>
      <p className="text-small text-muted">We gebruiken deze gegevens voor de inspectie en het attest.</p>

      <div className="form-row">
        <FormField
          id="firstName"
          label="Voornaam"
          required
          value={contact.firstName}
          onChange={(v) => patch({ firstName: v })}
          onBlur={() => markTouched("firstName")}
          error={err("firstName")}
          autoComplete="given-name"
        />
        <FormField
          id="lastName"
          label="Achternaam"
          required
          value={contact.lastName}
          onChange={(v) => patch({ lastName: v })}
          onBlur={() => markTouched("lastName")}
          error={err("lastName")}
          autoComplete="family-name"
        />
      </div>

      <div className="form-row">
        <FormField
          id="email"
          label="E-mailadres"
          type="email"
          required
          value={contact.email}
          onChange={(v) => patch({ email: v })}
          onBlur={() => markTouched("email")}
          error={err("email")}
          autoComplete="email"
          hint="We sturen de bevestiging en het attest naar dit adres."
        />
        <FormField
          id="emailConfirm"
          label="Bevestig e-mailadres"
          type="email"
          required
          value={contact.emailConfirm || ""}
          onChange={(v) => patch({ emailConfirm: v })}
          onBlur={() => markTouched("emailConfirm")}
          error={err("emailConfirm")}
          autoComplete="email"
        />
      </div>

      <div className="form-row">
        <FormField
          id="phone"
          label="Telefoonnummer"
          type="tel"
          required
          value={contact.phone}
          onChange={(v) => patch({ phone: v })}
          onBlur={() => markTouched("phone")}
          error={err("phone")}
          autoComplete="tel"
          inputMode="tel"
          hint="Voor afspraken en vragen."
        />
      </div>

      <div className="form-row">
        <FormField
          id="street"
          label="Straat"
          required
          value={contact.street}
          onChange={(v) => patch({ street: v })}
          onBlur={() => markTouched("street")}
          error={err("street")}
          autoComplete="address-line1"
        />
        <div className="form-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
          <FormField
            id="houseNumber"
            label="Huisnummer"
            required
            value={contact.houseNumber}
            onChange={(v) => patch({ houseNumber: v })}
            onBlur={() => markTouched("houseNumber")}
            error={err("houseNumber")}
            inputMode="numeric"
          />
          <FormField id="bus" label="Bus" value={contact.bus || ""} onChange={(v) => patch({ bus: v })} />
        </div>
      </div>

      <div className="form-row">
        <FormField
          id="postalCode"
          label="Postcode"
          required
          value={contact.postalCode}
          onChange={(v) => patch({ postalCode: v })}
          onBlur={() => markTouched("postalCode")}
          error={err("postalCode")}
          autoComplete="postal-code"
          inputMode="numeric"
        />
        <FormField
          id="city"
          label="Gemeente"
          required
          value={contact.city}
          onChange={(v) => patch({ city: v })}
          onBlur={() => markTouched("city")}
          error={err("city")}
          autoComplete="address-level2"
        />
      </div>

      <details className="form-disclosure" style={{ marginBottom: "var(--space-4)" }}>
        <summary className="form-disclosure__summary">
          Klik hier om een facturatieadres of ondernemingsnummer toe te voegen
        </summary>
        <div className="mt-4">
          <div className="checkbox-field">
            <input
              type="checkbox"
              id="billingDifferent"
              checked={Boolean(contact.billingDifferent)}
              onChange={(e: any) => patch({ billingDifferent: e.target.checked })}
            />
            <label htmlFor="billingDifferent">Facturatieadres wijkt af van het inspectieadres</label>
          </div>
          {contact.billingDifferent && (
            <FormField
              id="billingAddress"
              label="Facturatieadres"
              as="textarea"
              rows={2}
              value={contact.billingAddress || ""}
              onChange={(v) => patch({ billingAddress: v })}
            />
          )}
          <FormField
            id="vatNumber"
            label="Ondernemingsnummer"
            value={contact.vatNumber || ""}
            onChange={(v) => patch({ vatNumber: v })}
            onBlur={() => markTouched("vatNumber")}
            error={err("vatNumber")}
            placeholder="BE0123456789"
          />
        </div>
      </details>

      <FormField
        id="notes"
        label="Aanvullende informatie (optioneel)"
        as="textarea"
        rows={3}
        value={contact.notes || ""}
        onChange={(v) => patch({ notes: v })}
      />
    </div>
  );
}
